export interface FilterChip {
	key: string | null;
	label: string;
	value: string;
}

export interface TagDef {
	key: string;
	label: string;
	description?: string;
	searchable?: boolean;
	allowMultiple?: boolean;
	values?: string[];
	displayTransform?: (rawValue: unknown) => string;
}

function normalizeSearchValue(value: unknown): string {
	return String(value ?? "").toLowerCase();
}

function getNormalizedTagValues(tag: TagDef, raw: unknown): string[] {
	if (Array.isArray(raw)) {
		return raw
			.map(value =>
				normalizeSearchValue(
					tag.displayTransform
						? tag.displayTransform(value)
						: value,
				),
			)
			.filter(Boolean);
	}

	return [
		normalizeSearchValue(
			tag.displayTransform ? tag.displayTransform(raw) : raw,
		),
	];
}

/**
 * Applies plain-text search and tag-specific search to a dataset.
 * A chip is a single search term, (optionally) associated with a specific tag key.
 *
 * Plain-text search performs substring matches across all searchable tags.
 * Tag-specific chips are grouped by tag key and compared against that tag's normalized display value.
 * Multi-select tags allow more than one chip for the same tag; the row must match every selected chip.
 *
 * @param data The dataset to filter.
 * @param chips The committed search chips to apply.
 * @param tags The tag definitions that describe which fields are searchable and how values are displayed.
 * @param liveQuery Optional uncommitted plain-text input to apply alongside the committed chips.
 * @returns The subset of `data` that matches every active search condition.
 */
export function applyChipFilters<T extends object>(
	data: T[],
	chips: FilterChip[],
	tags: TagDef[],
	liveQuery?: string,
): T[] {
	// Keep the incoming chip list immutable while treating the live query
	// as one additional plain-text search term during filtering.
	const effectiveChips: FilterChip[] = liveQuery
		? [
				...chips,
				{ key: null, label: liveQuery, value: liveQuery.toLowerCase() },
			]
		: chips;

	if (!effectiveChips.length) return data;

	const searchableTags = tags.filter(tag => tag.searchable !== false);

	const chipsByKey = Map.groupBy(effectiveChips, ({ key }) => key);

	return data.filter(row =>
		// Convert the map entries to an array to call `every()`.
		[...chipsByKey.entries()].every(([chipKey, groupedChips]) => {
			const record = row as Record<string, unknown>;

			if (chipKey === null) {
				return groupedChips.every(chip =>
					searchableTags.some(tag => {
						const raw = record[tag.key];
						return getNormalizedTagValues(tag, raw).some(display =>
							display.includes(chip.value),
						);
					}),
				);
			}

			const tag = searchableTags.find(
				searchableTag => searchableTag.key === chipKey,
			);
			if (!tag) return true;

			const raw = record[tag.key];
			const values = getNormalizedTagValues(tag, raw);

			if (tag.allowMultiple) {
				return groupedChips.every(chip => values.includes(chip.value));
			}

			return groupedChips.every(chip => values.includes(chip.value));
		}),
	);
}
